import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, Plus, Trash2, ChevronDown, ChevronRight, Send } from 'lucide-react';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

const inputCls = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500";
const labelCls = "block text-xs font-medium text-stone-600 mb-1";
const htmlCls = "w-full px-3 py-2 text-xs font-mono border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-500";

// Merge tokens admins can use inside any subject/body. Display-only reference; clicking
// copies the token to the clipboard for convenience.
const MERGE_TOKENS = [
    '{{FirstName}}', '{{LastName}}', '{{LeadEmail}}',
    '{{CoachName}}', '{{CoachFirstName}}', '{{CoachEmail}}', '{{CoachPhone}}', '{{CoachLinkedIn}}', '{{CoachTitle}}', '{{CoachBio}}', '{{CoachPhoto}}', '{{CoachPhotoImg}}',
    '{{MeetingDate}}', '{{MeetingTime}}', '{{MeetingDateTime}}', '{{Duration}}', '{{Timezone}}',
    '{{MeetLink}}', '{{RescheduleLink}}', '{{BookingPageUrl}}', '{{LeadRecordUrl}}', '{{OrgName}}', '{{BrandColor}}',
];

const DEFAULT_SETTINGS = {
    coach_intro: { enabled: false, subject: '', html: '' },
    coach_intro_delay_minutes: 0,
    confirmation: { enabled: false, subject: '', html: '' },
    coach_notification: { enabled: false, subject: '', html: '' },
    reschedule: { enabled: false, subject: '', html: '' },
    cancellation: { enabled: false, subject: '', html: '' },
    reminders: [],
    booking_page_url: '',
    brand_color: '#1c1917',
};

// Reusable email-block editor (Coach intro / Confirmation / Coach notification).
function EmailBlock({ title, block, onChange, children }) {
    const b = block || { enabled: false, subject: '', html: '' };
    return (
        <div className="rounded-xl border border-stone-200 bg-white p-4">
            <label className="flex items-center justify-between gap-2 mb-3 cursor-pointer">
                <span className="text-sm font-semibold text-stone-800">{title}</span>
                <span className="flex items-center gap-2 text-xs text-stone-500">
                    <input type="checkbox" checked={!!b.enabled}
                        onChange={e => onChange({ ...b, enabled: e.target.checked })}
                        className="rounded border-stone-300" />
                    Enabled
                </span>
            </label>
            <div className="space-y-3">
                <div>
                    <label className={labelCls}>Subject</label>
                    <input value={b.subject || ''} onChange={e => onChange({ ...b, subject: e.target.value })} className={inputCls} />
                </div>
                <div>
                    <label className={labelCls}>HTML body</label>
                    <textarea rows={8} value={b.html || ''} onChange={e => onChange({ ...b, html: e.target.value })} className={htmlCls} spellCheck={false} />
                </div>
                {children}
            </div>
        </div>
    );
}

export default function BookingAutomationPanel() {
    const { getAuthHeader } = useAuth();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testWhich, setTestWhich] = useState('confirmation');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/booking-automation/settings`, { headers: getAuthHeader() });
            setSettings({ ...DEFAULT_SETTINGS, ...(res.data || {}) });
        } catch {
            toast.error('Failed to load booking automation settings');
            setSettings({ ...DEFAULT_SETTINGS });
        } finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { load(); }, [load]);

    const patch = (p) => setSettings(s => ({ ...(s || DEFAULT_SETTINGS), ...p }));

    const setReminder = (idx, p) => setSettings(s => {
        const reminders = [...(s.reminders || [])];
        reminders[idx] = { ...reminders[idx], ...p };
        return { ...s, reminders };
    });
    const addReminder = () => setSettings(s => ({
        ...s, reminders: [...(s.reminders || []), { offset_hours: 24, enabled: true, subject: '', html: '' }],
    }));
    const removeReminder = (idx) => setSettings(s => ({
        ...s, reminders: (s.reminders || []).filter((_, i) => i !== idx),
    }));

    const handleSave = async () => {
        if (!settings) return;
        setSaving(true);
        try {
            const res = await axios.put(`${API}/api/admin/booking-automation/settings`, settings, { headers: getAuthHeader() });
            setSettings({ ...DEFAULT_SETTINGS, ...(res.data || settings) });
            toast.success('Saved');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to save settings');
        } finally { setSaving(false); }
    };

    const handleTestSend = async () => {
        setTesting(true);
        try {
            const res = await axios.post(`${API}/api/admin/booking-automation/test-send`, { which: testWhich }, { headers: getAuthHeader() });
            toast.success('Test sent to ' + (res.data?.to || 'you'));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to send test');
        } finally { setTesting(false); }
    };

    const copyToken = (tok) => {
        try { navigator.clipboard.writeText(tok); toast.success(`Copied ${tok}`); } catch { /* clipboard may be blocked */ }
    };

    return (
        <div className="mb-5 rounded-xl border border-stone-200 bg-stone-50">
            {/* Header (click to collapse/expand) */}
            <button type="button" onClick={() => setCollapsed(c => !c)}
                className="w-full flex items-center justify-between gap-3 p-4 text-left">
                <div>
                    <p className="text-sm font-semibold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Booking Automation</p>
                    <p className="text-xs text-stone-500 mt-0.5">Branded emails sent around assignment + booking.</p>
                </div>
                {collapsed ? <ChevronRight className="w-5 h-5 text-stone-400 shrink-0" /> : <ChevronDown className="w-5 h-5 text-stone-400 shrink-0" />}
            </button>

            {!collapsed && (
                <div className="px-4 pb-4">
                    {loading || !settings ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
                    ) : (
                        <div className="space-y-4">
                            {/* Merge variables reference */}
                            <div className="rounded-xl border border-stone-200 bg-white p-4">
                                <p className="text-xs font-medium text-stone-600 mb-2">Merge variables <span className="text-stone-400 font-normal">— click to copy</span></p>
                                <div className="flex flex-wrap gap-1.5">
                                    {MERGE_TOKENS.map(tok => (
                                        <button key={tok} type="button" onClick={() => copyToken(tok)}
                                            className="px-2 py-0.5 rounded-md text-[11px] font-mono border border-stone-200 bg-stone-50 text-stone-600 hover:border-stone-400 hover:bg-stone-100">
                                            {tok}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Coach intro (with send delay) */}
                            <EmailBlock title="Coach intro" block={settings.coach_intro} onChange={v => patch({ coach_intro: v })}>
                                <div className="w-48">
                                    <label className={labelCls}>Send delay (minutes)</label>
                                    <input type="number" min="0" className={inputCls}
                                        value={settings.coach_intro_delay_minutes ?? 0}
                                        onChange={e => patch({ coach_intro_delay_minutes: e.target.value === '' ? 0 : Number(e.target.value) })} />
                                </div>
                            </EmailBlock>

                            {/* Confirmation */}
                            <EmailBlock title="Confirmation" block={settings.confirmation} onChange={v => patch({ confirmation: v })} />

                            {/* Coach notification */}
                            <EmailBlock title="Coach notification" block={settings.coach_notification} onChange={v => patch({ coach_notification: v })} />

                            {/* Reminders */}
                            <div className="rounded-xl border border-stone-200 bg-white p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-sm font-semibold text-stone-800">Reminders</span>
                                    <Button variant="outline" size="sm" onClick={addReminder}><Plus className="w-4 h-4 mr-1.5" /> Add reminder</Button>
                                </div>
                                {(settings.reminders || []).length === 0 ? (
                                    <p className="text-xs text-stone-400 py-2">No reminders configured.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {(settings.reminders || []).map((r, idx) => (
                                            <div key={idx} className="rounded-lg border border-stone-200 bg-stone-50 p-3 space-y-3">
                                                <div className="flex items-end gap-3">
                                                    <div className="w-40">
                                                        <label className={labelCls}>Hours before call</label>
                                                        <input type="number" min="0" className={inputCls}
                                                            value={r.offset_hours ?? 0}
                                                            onChange={e => setReminder(idx, { offset_hours: e.target.value === '' ? 0 : Number(e.target.value) })} />
                                                    </div>
                                                    <label className="flex items-center gap-2 text-xs text-stone-500 pb-2.5 cursor-pointer">
                                                        <input type="checkbox" checked={!!r.enabled}
                                                            onChange={e => setReminder(idx, { enabled: e.target.checked })}
                                                            className="rounded border-stone-300" />
                                                        Enabled
                                                    </label>
                                                    <div className="flex-1" />
                                                    <button onClick={() => removeReminder(idx)} title="Remove reminder"
                                                        className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg mb-1">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <div>
                                                    <label className={labelCls}>Subject</label>
                                                    <input value={r.subject || ''} onChange={e => setReminder(idx, { subject: e.target.value })} className={inputCls} />
                                                </div>
                                                <div>
                                                    <label className={labelCls}>HTML body</label>
                                                    <textarea rows={8} value={r.html || ''} onChange={e => setReminder(idx, { html: e.target.value })} className={htmlCls} spellCheck={false} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Reschedule */}
                            <EmailBlock title="Reschedule" block={settings.reschedule} onChange={v => patch({ reschedule: v })} />

                            {/* Cancellation */}
                            <EmailBlock title="Cancellation" block={settings.cancellation} onChange={v => patch({ cancellation: v })} />

                            {/* Booking page URL + brand color */}
                            <div className="rounded-xl border border-stone-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>Booking page URL</label>
                                    <input value={settings.booking_page_url || ''} onChange={e => patch({ booking_page_url: e.target.value })} className={inputCls} placeholder="https://…" />
                                    <p className="text-[11px] text-stone-400 mt-1">CTA link used in the coach-intro email.</p>
                                </div>
                                <div>
                                    <label className={labelCls}>Brand color</label>
                                    <div className="flex items-center gap-2">
                                        <input type="color" value={settings.brand_color || '#1c1917'}
                                            onChange={e => patch({ brand_color: e.target.value })}
                                            className="h-9 w-12 rounded-lg border border-stone-200 bg-white p-1 cursor-pointer" />
                                        <input value={settings.brand_color || ''} onChange={e => patch({ brand_color: e.target.value })}
                                            className={inputCls} placeholder="#1c1917" />
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                                <div className="flex items-center gap-2">
                                    <select className={inputCls + ' w-auto'} value={testWhich} onChange={e => setTestWhich(e.target.value)} disabled={testing}>
                                        <option value="confirmation">Confirmation</option>
                                        <option value="coach_intro">Coach intro</option>
                                        <option value="coach_notification">Coach notification</option>
                                        <option value="reschedule">Reschedule</option>
                                        <option value="cancellation">Cancellation</option>
                                        <option value="reminder">Reminder</option>
                                    </select>
                                    <Button variant="outline" disabled={testing} onClick={handleTestSend}>
                                        {testing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-1.5" /> Send test to me</>}
                                    </Button>
                                </div>
                                <Button onClick={handleSave} disabled={saving}>
                                    {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
